import {
  Mesh,
  DoubleSide,
  FrontSide,
  VideoTexture,
  ShaderMaterial,
  BufferGeometry,
  BufferAttribute,
  MeshBasicMaterial
} from 'three';

import gsap, { RoughEase, Linear } from 'gsap/all';
import { SCENE_INTERACTION } from '../../constants/scene';
import { NEW_WEBRTC_EVENTS, WEBRTC_EVENTS } from '../../constants/webrtc';

import { VRProfileHelper } from './VRProfileHelper';

import {
  chairCircleGeometry,
  chairPlaneGeometry,
  textureLoader,
  defaultTexture,
  screenShareTexture,
  imageGalleryLoading
} from './helpers/texturesAndGeometries';

import { SOCKET_EVENTS } from '../../socket/Socket';
import { ANT_MEDIA_SERVER_BAGELSS_EVENTS, states } from '../WebRTC/constants';
import { createRightMaterial } from './constants/rightImageShader';
import { SCENE_EVENTS } from '../MVC/Model';


class VRScreenShare extends VRProfileHelper {

  constructor(containerName, camera, texture, x, y, z, model) {

    super(containerName, camera, texture, model);

    this.iSitOnIt = false;
    this.muted = false;
    this.emptySeat = true;

    this.limits = {
      min: 115,
      max: 570,
      distance: 300
    }
    this.isVideoStarted = false;

    this.isVideoTexture = false;
    this.videoElement = null;
    this.videoTexture = null;

    this.requestAudioLevels = false;
    this.averageVolume = 0;
    this.requestFakeAnimation = true;
    this.firstTime = true;

    this.material = new MeshBasicMaterial({ map: screenShareTexture, side: DoubleSide, transparent: false, premultipliedAlpha: true });
    this.videoMaterial = new MeshBasicMaterial({
      side: FrontSide,
      transparent: true,
      depthWrite: true,
      depthTest: true,
    });
    this.waitingMaterial = new MeshBasicMaterial({ transparent: true, premultipliedAlpha: true });
    this.emptySeatTexture = texture;

    this.mesh = new Mesh(this.model.editMode ? chairPlaneGeometry : chairCircleGeometry, this.waitingMaterial);

    this.mesh.renderOrder = 1;
    this.mesh.type = 'screenStream';
    this.mesh.name = 'screenStream';
    this.name = 'screenStream';

    this.isPublic = this.model.publicStage;

    this.add(this.mesh);
    this.borderThickness = 14;
    // cache key to avoid unnecessary geometry rebuilds
    this._lastGeometryKey = null;

    this.camera !== null ? this._setInitialPosRot() : this.setPosition(x, y, z);

    this.mesh.groupDetails = { title: $trans('chair'), icon: 'chair', editTitle: $trans('edit-chair'), dataLang: 'edit-chair' }
    !this.model.editMode && this.addBorder();
    // ensure border is visible by default for screen share
    if (this.borderMesh) {
      this.borderMesh.visible = true;
    }

    screen.orientation && screen.orientation.addEventListener('change', this.fixRaportRatio); // TODO - walkaround for screen on safari browser

    //// camera / avatar for USER handler
    this.handleUserWantsCamera = () => {
      // const video = this.model.getCameraHTMLObject();
      const video = document.getElementById('screenVideoANT');
      this.startVideo(video);
      this.takeSeat(true);
      this.fixRaportRatio();
    }
    this.handleUserWantsAvatar = () => {
      this.setAvatarImage(this.model.config.userAvatarUrl)
      this.takeSeat(true);
    }

    ///new 
    this.occupedWithoutStream = () => {
      const state = this.getState();
      if (state.occupied) {
        if (state.me) {
          if (state.videoOn) {
            this.handleUserWantsCamera();
          } else {
            this.handleUserWantsAvatar();
          }
        } else {
          if (state.videoOn) {
            this.startVideoStream(state.screenShareStreamId)
          } else {
            this.setAvatarImage(state.avatarImage)
          }
        }
      }
      else {
        this.standUp();
      }
    }

    this.isSeatOccupiedByUser = () => {
      if (this.model.editMode) {
        return;
      }
      this.iSitOnIt = false;
      const occupiedSeats = this.model.socket.getOccupiedScreenShareSeats();
      if (occupiedSeats[this.dbConfig.id] && occupiedSeats[this.dbConfig.id] === this.model.config.userId) {
        this.iSitOnIt = true;
        this.model.iShareOnThisObj = this;
      }
      if (!occupiedSeats[this.dbConfig.id]) {
        this.screenShareStreamId = null;
        this.standUp()
      }
      if (occupiedSeats[this.dbConfig.id]) {
        this.occupySeat(this.model.socket.getUsersObject()[occupiedSeats[this.dbConfig.id]])
      }
      return occupiedSeats[this.dbConfig.id];
    }

    this.model.addEventListener(SCENE_INTERACTION.SCENE_INITIALISED, (e) => {
      this.occupedWithoutStream();
      const state = this.getState();
      this.showRightMaterial();
      if (!state.occupied) {
        this.standUp();
      }
    });
    this.model.addEventListener(SCENE_INTERACTION.USER_LEAVES_SCREEN_SHARE, (e) => {
      this.occupedWithoutStream();
      const state = this.getState();
      this.showRightMaterial();
      if (!state.occupied) {
        this.standUp();
      }
    });

    this.model.addEventListener(ANT_MEDIA_SERVER_BAGELSS_EVENTS.LEAVE_ROOM_SUCCESS_SCREEN_SHARE, (e) => {
      this.occupedWithoutStream();
    });
    this.model.addEventListener(ANT_MEDIA_SERVER_BAGELSS_EVENTS.USER_SWITCHED_VIDEO_TO_SCREEN, (e) => {
      this.fixRaportRatio(480, 640);
    });
    this.model.addEventListener(ANT_MEDIA_SERVER_BAGELSS_EVENTS.VIDEO_CREATED, (e) => {
      this.showRightMaterial();
    });
    this.model.socket.addEventListener(SOCKET_EVENTS.INIT_USERS, (e) => {
      this.showRightMaterial();
    });
    this.model.addEventListener(ANT_MEDIA_SERVER_BAGELSS_EVENTS.SCREEN_SHARE_VIDEO_PLAY_STARTED, (e) => {
      this.setScreenGeometry();
      this.showBorder();
      this.setBorderYellow();
      this.borderMesh.material.opacity = .3;
      gsap.to(this.borderMesh.material, {
        opacity: 0,
        duration: .5,
        repeat: -1,
        yoyo: true,
        ease: "power2.inOut",
      });
      this.model.info('info', $trans('info-waiting-for-screen-share-users'));
    });
    this.model.addEventListener(ANT_MEDIA_SERVER_BAGELSS_EVENTS.SCREEN_SHARE_STREAM_RECIEVED, (e) => {
      this.setBorderGreen();
      ///animate smooth outro animation with gsap and on end hide border, a
      ///kill gsap if exists
      gsap.killTweensOf(this.borderMesh.material);
      this.borderMesh.material.opacity = .3;
      gsap.to(this.borderMesh.material, {
        opacity: 0,
        duration: 1,
        delay: 2,
        ease: "power2.inOut",
        onComplete: () => {
          this.hideBorder();
        }
      });
      this.model.info('info', $trans('info-screen-share-started-users'));
    });

    ////user started share publish, due to delayed call need to recreate texture, happends once
    this.model.addEventListener("USER_STARTED_SCREEN_SHARE_PUBLISH", (e) => {
      const occupiedBy = this.isSeatOccupiedByUser()
      if (occupiedBy && (occupiedBy === this.model.config.userId)) {
        ////its me
        const video = this.model.getScreenShareHTMLObject();
        this.handleUserWantsCamera();
        // const data = this.model.socket.getUsers()[occupiedBy];
        // const { userAvatarUrl, userVideoOn } = data;
        // if (!userVideoOn) {
        //   this.setAvatarImage(userAvatarUrl)
        // }
      }
    });



    ///turn on and off camera 
    this.model.addEventListener(SOCKET_EVENTS.USER_VIDEO_STATE_CHANGED, (e) => {
      const state = this.getState();
      if (state.occupied) {
        if (state.me) {
          if (state.videoOn) {
            this.handleUserWantsCamera();
          } else {
            this.handleUserWantsAvatar();
          }
        } else {
          if (state.videoOn) {
            this.startVideoStream(state.screenShareStreamId)
          } else {
            this.setAvatarImage(state.avatarImage)
          }
        }
      }
      else {
        this.standUp();
      }
    });
    this.model.addEventListener("ctaMenuClickEvents", (e) => {
      const occupiedBy = this.isSeatOccupiedByUser()
      if (occupiedBy) {
        this.mesh.visible = true;
        if (occupiedBy === this.model.config.userId) {
          ///its me
          if (this.model.userWantsCamera) {
            this.handleUserWantsCamera();
          } else {
            if (this.type !== "screenStream")
              this.handleUserWantsAvatar();
          }
        }
      }
    });
    this.model.addEventListener(ANT_MEDIA_SERVER_BAGELSS_EVENTS.STREAM_INFO, (e) => {
      this.fixRaportRatio();
    });

    this.model.addEventListener(SCENE_INTERACTION.SET_OCCUPIED_SCREEN_SHARE_SEATS, async (e) => {
      const state = this.getState();
      this.showRightMaterial();
      if (!state.occupied) {
        this.standUp();
      }
    });
    this.model.addEventListener(SCENE_INTERACTION.USER_STOP_SCREEN, async (e) => {
      const occupiedBy = this.isSeatOccupiedByUser()
      if (occupiedBy) {
        this.mesh.visible = true;
        if (occupiedBy === this.model.config.userId) {
          ////its me
          this.mesh.material = this.material;
          this.mesh.visible = false;
        }
      }
    });
    //// ant media recieved stream
    this.model.addEventListener("TEST_RE_CREATE_WEB_STREAMS", async (e) => {
      const occupiedBy = this.isSeatOccupiedByUser()
      if (occupiedBy) {
        if (occupiedBy === this.model.config.userId) {
          ////its me
          if (this.model.userWantsCamera) {
            this.handleUserWantsCamera()
          } else {
            this.handleUserWantsAvatar();
          }
        }
        else {
          const data = this.model.socket.getUsersObject()[occupiedBy];
          const { screenShareStreamId, userAvatarUrl, userVideoOn } = data;
          console.log("screenShareStreamId")
          if (screenShareStreamId && (screenShareStreamId !== this.screenShareStreamId)) {
            this.startVideoStream(screenShareStreamId);
          }
          if (!userVideoOn) {
            this.setAvatarImage(userAvatarUrl)
          }

        }
      }
      else {
        this.mesh.material = this.material;
      }
    });

    this.model.addEventListener(SCENE_INTERACTION.SOCKET_USER_INFO_CHANGED, async (e) => {
      const state = this.getState();
      if (state.occupied) {
        this.showRightMaterial();
      }
      else {
        this.standUp()
      }
    });

    this.model.addEventListener("REMOVE_STREAM", async (e) => {
      if (this.screenShareStreamId && this.screenShareStreamId === e.data.id) {
        this.mesh.material = this.material;
      }
      this.screenShareStreamId = null;
    });

    this.startVideoStream = async (screenShareStreamId) => {
      this.screenShareStreamId = screenShareStreamId;
      const state = this.getState();
      if (state.occupied) {
        if (state.me) {
          if (state.videoOn) {
            this.handleUserWantsCamera();
          } else {
            this.handleUserWantsAvatar();
          }
        } else {
          if (state.videoOn) {
            if (this.videoIsPlaying) {
              return;
            }
            this.mesh.material = this.waitingMaterial;
            this.mesh.visible = false;
            this.loadingScreen.add(this.state.avatarImage);
            if (this.videoElement && !this.videoElement.srcObject) {
              if (this.videoMaterial) {
                this.videoMaterial.map = null;
              }
              if (this.videoTexture) {
                this.videoTexture.dispose();
                this.videoTexture = null;
              }
              this.videoElement = null;
            }
            if (!document.getElementById(`remoteVideo-${screenShareStreamId}`)) {
              return;
            } else {
              const videoElement = document.getElementById(`remoteVideo-${screenShareStreamId}`);
              this.videoElement = videoElement;
              videoElement.addEventListener("play", () => {
                console.log("play");
              });
              videoElement.addEventListener("loadedmetadata", () => {
                console.log("loadedmetadata");
              });
              videoElement.addEventListener("canplaythrough", () => {
                console.log("canplaythrough");
              });
              videoElement.addEventListener("error", () => {
                console.log("error");
              });
              videoElement.addEventListener("stalled", () => {
                console.log("stalled");
              });
              videoElement.addEventListener("waiting", () => {
                console.log("waiting");
              });
              videoElement.addEventListener("suspend", () => {
                console.log("suspend");
              });
              videoElement.addEventListener("stalled", () => {
                console.log("stalled");
              });
              videoElement.addEventListener("waiting", () => {
                console.log("waiting");
              });
              videoElement.addEventListener("suspend", () => {
                console.log("suspend");
              });
              videoElement.addEventListener("abort", () => {
                console.log("abort");
                this.videoIsPlaying = false;
              });
              videoElement.addEventListener("emptied", () => {
                console.log("emptied");
                this.videoIsPlaying = false;
              });
              videoElement.addEventListener("stalled", () => {
                console.log("stalled");
              });
              videoElement.addEventListener("waiting", () => {
                console.log("waiting");
              });
              const callback = () => {
                this.startVideo(videoElement);
                this.videoIsPlaying = true;
                videoElement.removeEventListener("timeupdate", callback);
              }
              videoElement.addEventListener("timeupdate", callback, { once: true });
            }
            this.videoElement && this.audioLevelsAnalyzer(this.videoElement.srcObject);
          } else {
            this.setAvatarImage(state.avatarImage)
          }
        }
      }
      else {
        this.standUp();
      }
    }
    this.mesh.visible = true;
    ///new x
    return this;
  }

  // Border controls
  showBorder() {
    if (this.borderMesh) {
      this.borderMesh.visible = true;
    }
  }

  hideBorder() {
    if (this.borderMesh) {
      this.borderMesh.visible = false;
      this.borderMesh.material.opacity = 1;
    }
  }

  setBorderColor(colorHex) {
    if (this.borderMaterial) {
      this.borderMaterial.color.setHex(colorHex);
      this.borderMaterial.needsUpdate = true;
    }
    this.showBorder();
  }

  setBorderRed() {
    this.setBorderColor(0xff0000);
  }

  setBorderGreen() {
    this.setBorderColor(0x00ff00);
  }

  setBorderYellow() {
    this.setBorderColor(0xffff00);
  }

  setBorderWidth(width) {
    const parsed = Number(width);
    if (Number.isFinite(parsed) && parsed >= 0) {
      this.borderThickness = parsed;
      // Rebuild geometry if we have enough info
      if (this.videoElement && this.videoElement.srcObject) {
        // invalidate cache so a rebuild happens with new thickness
        this._lastGeometryKey = null;
        this.setScreenGeometry();
      } else if (this.lastKnownAspectRatio) {
        // approximate geometry when no active video by using last aspect ratio
        let w = 1;
        let h = 1;
        const aspectRatio = this.lastKnownAspectRatio;
        if (aspectRatio < 1) {
          w = 360 * aspectRatio;
          h = 360;
        } else {
          w = 480;
          h = 480 / aspectRatio;
        }
        if (!this.borderMesh) return;
        const videoBorderGeometry = this.getBorder(w + this.borderThickness, h + this.borderThickness, 2);
        this.borderMesh.geometry = new BufferGeometry();
        this.borderMesh.geometry.setAttribute('position', new BufferAttribute(new Float32Array(videoBorderGeometry[0]), 3));
        this.borderMesh.geometry.setAttribute('uv', new BufferAttribute(new Float32Array(videoBorderGeometry[1]), 2));
        this.borderMesh.translateZ(-2);
        this.borderMesh.visible = true;
        this._lastGeometryKey = `${aspectRatio}|${this.borderThickness}`;
      }
    }
  }

  fixRaportRatio(streamWidth, streamHeight) {
    return;
  }

  setMyMood() {
    // const mood = document.querySelector("#user_mood").classList[0].replace("mood_", "");
    // this.showOrUpdateMood(mood);
  }

  toggleAudio() {
    this.state = this.getState();
    if (this.model.screenShareMuted && this.state.me) {
      this.model.dispatchEvent({ type: SCENE_EVENTS.USER_SCREEN_SHARE_TURN_MIC_ON })
    }
    if (!this.model.screenShareMuted && this.state.me) {
      this.model.dispatchEvent({ type: SCENE_EVENTS.USER_SCREEN_SHARE_TURN_MIC_OFF })
    }
    this.model.screenShareMuted = !this.model.screenShareMuted;
  }


  updateUserVideoState(state) {
    // console.log('updateUserVideoState ', state);
    if (this.videoElement !== null && this.videoElement !== undefined) {
      this.mesh.material = state ? this.videoMaterial : this.material;
      state ? $(this.videoElement).parent().removeClass('hidden') : $(this.videoElement).parent().addClass('hidden');
    }
  }

  useAvatarMaterial = (texture) => {
    // console.log('useAvatarMaterial');
    // this.mesh.material = this.material;
    // this.material.map = texture;
    // this.isVideoTexture = false;
    // this.setMyMood();
  }

  useVideoMaterial() {
    // if (this.startVideo(document.getElementById('myVideoScreenShare'))) {
    //   this.isVideoTexture = true;
    // } else {
    //   this.model.alert('error', $trans('error-turn-on-camera'));
    // }
    // this.setMyMood();
  }

  takeSeat() {
    this.mesh.visible = true;
    $(`.indicator-${this.mesh.id}`).addClass('taken');
    $("#stopScreenShare").hide();
    $("#toggleGridModeScreenShare").hide();
    if (this.model.view.iSitOnScreenStream()) {
      $("#stopScreenShare").show();
    }
    return;

    // console.log('takeSeat');
    // this.videoElement = videoId !== null ? document.getElementById(videoId) : null;
    // this.iSitOnIt = true;
    // this.emptySeat = false;

    // this.mesh.visible = true;
    // $(`.indicator-${this.mesh.id}`).addClass('taken');

    // if (this.iSitOnIt) {
    //   if (texture !== null) {
    //     this.material.map = texture;
    //   }
    //   this.audioLevelsAnalyzer(this.videoElement.srcObject);
    //   isVideoTextureOn ? this.useVideoMaterial() : this.useAvatarMaterial(texture);

    // }
    this.mesh.visible = true;
  }

  standUp() {
    if (this.model.editMode || this.userData === null)
      return;
    this.mesh.visible = false;
    this.firstTime = false;
    $(`.indicator-${this.mesh.id}`).removeClass('taken');
    this.hideUserOptions();
    this.userData = null;
    // if (this.stream) {
    //   this.model.killStream(this.stream);
    //   this.stream = null;
    //   if (this.model.screenShareStream) {
    //     this.model.killStream(this.model.screenShareStream)
    //   }
    // }
    this.screenStream && (this.screenStream.removeEventListener('ended', this.onScreenStreamEnded));
    if (this.videoMaterial) {
      this.videoMaterial.map = null;
    }
    if (this.videoTexture) {
      this.videoTexture.dispose();
      this.videoTexture = null;
    }
    this.loadingScreen.remove();
    return;
  }

  occupySeat(userData) {
    this.userData = userData;
    this.emptySeat = false;
    // if (this.userData) {
    //   this.mesh.material = this.waitingMaterial;
    // }
    this.takeSeat();
    if (!this.state.me && this.state.occupied && !this.videoIsPlaying) {
      this.mesh.material = this.waitingMaterial;
      if (this.state.videoOn) {
        const imageSrc = this.state.avatarImage;
        this.loadingScreen.add(imageSrc);
        this.mesh.visible = false;
        this.model.info('info', $trans('info-waiting-for-screen-share'));
      }
    }
  }

  setBusyTexture = async (on1on1Call, userId) => {
    // // console.log('setBusy Texture' ,on1on1Call);
    // if (on1on1Call) {
    //   $('#remoteVideo_' + userId).addClass('hidden');
    //   $('#remoteVideo_' + userId).addClass('on-1on1-call');
    //   console.log($('#remoteVideo_' + userId));
    // } else {
    //   $('#remoteVideo_' + userId).removeClass('hidden');
    //   $('#remoteVideo_' + userId).removeClass('on-1on1-call');
    // }
    // this.busyMesh.visible = on1on1Call;
  }

  setAvatarImage(url, apply = true) {
    if (!url)
      return;
    console.log('setAvatarImage');

    fetch(url, { method: 'HEAD' })
      .then(res => {
        if (res.ok) {
          //console.log('Image exists.');
          const tex = textureLoader.load(url);
          this.material.map = tex;
          if (apply) {
            this.mesh.material = this.material;
          }
        } else {
          // console.log('Image does not exist.');
          //when from app.bagless.io or CORS problem
          this.material.map = defaultTexture;
          if (apply) {
            this.mesh.material = this.material;
          }
        }
      }).catch(err => console.log('Error:', err));
  }

  startVideo(videoElement, userVideoOn = true, streamId, streamWidth, streamHeight) {
    if (userVideoOn) {
      if (this.videoTexture && this.videoTexture.image === videoElement && this.videoMaterial.map === this.videoTexture) {
        return true;
        this.mesh.material = this.videoMaterial;
        this.setScreenGeometry();
        this.audioLevelsAnalyzer(videoElement.srcObject);
        this.loadingScreen.remove();
      }
      this.videoElement = videoElement;
      this.mesh.material = this.videoMaterial;
      if (streamWidth && streamHeight && streamWidth != 0 && streamHeight != 0) {
        // console.log('fixRaportRatio wh', streamWidth,streamHeight);
        this.fixRaportRatio(streamWidth, streamHeight);
      } else {
        if (streamHeight) {
          // console.log('data-width ',videoElement.getAttribute('stream-width'));
          this.fixRaportRatio(streamWidth, streamHeight);
        } else {
          this.fixRaportRatio();
        }
      };
      if (!this.videoTexture) {
        this.videoTexture = new VideoTexture(videoElement);
      } else if (this.videoTexture.image !== videoElement) {
        this.videoTexture.image = videoElement;
        this.videoTexture.needsUpdate = true;
      }
      this.fixRaportRatio();
      this.takeSeat();
      this.loadingScreen.remove();
      this.videoMaterial.map = this.videoTexture
    }
    this.mesh.visible = true;
    this.audioLevelsAnalyzer(videoElement.srcObject);
    this.model.socket.onScreenShareStreamRecieved(videoElement.id);
    return true;
  }

  isEmpty() {
    if (this.isSeatOccupiedByUser()) {
      return false
    }
    return true
    // return this.emptySeat;
  }

  isMine() {
    return this.state.me;
  }

  audioLevelsAnalyzer = (stream) => {
    return;
    if (this.requestAudioLevels || !stream) {
      return
    }
    const audioContext = new AudioContext();
    const audioSource = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.4;
    audioSource.connect(analyser);
    const volumes = new Uint8Array(analyser.frequencyBinCount);

    this.requestAudioLevels = true;
    let firstSend = true;

    const levelsLoop = () => {
      return;
      setTimeout(() => {

        analyser.getByteFrequencyData(volumes);
        let volumeSum = 0;
        for (const volume of volumes) {
          volumeSum += volume;
        }
        this.averageVolume = Math.round(volumeSum / volumes.length);
        const scaleAmplitude = parseFloat(`1.${this.averageVolume / 20}`);
        this.moodMesh.scale.set(scaleAmplitude, scaleAmplitude, 1);

        if (firstSend) {
          this.iSitOnIt && this.model.dispatchEvent({ type: WEBRTC_EVENTS.WEBRTC_USER_IS_SPEAKING, data: { audioLevels: this.averageVolume } });
          firstSend = false;
        }

        this.requestAudioLevels && levelsLoop();

      }, 100);
    }
    levelsLoop();

    const updateLevelsOnServerLoop = () => {
      setTimeout(() => {
        this.model.dispatchEvent({ type: WEBRTC_EVENTS.WEBRTC_USER_IS_SPEAKING, data: { audioLevels: this.averageVolume } });

        this.requestAudioLevels && updateLevelsOnServerLoop();

      }, 1000);
    }
    this.iSitOnIt && updateLevelsOnServerLoop();



  }

  stopAudioLevelsAnalyzer() {
    this.requestAudioLevels = false;
    this.averageVolume = 0;
    this.moodMesh.scale.set(1, 1, 1);
  }

  fakeAudioLevelsAnimation(amplitude) {
    gsap.to(this.moodMesh.scale, {
      duration: .5,
      x: amplitude,
      y: amplitude,
      ease: RoughEase.ease.config({ strength: 8, points: 5, template: Linear.easeNone, randomize: false }),
      onComplete: () => {
        gsap.to(this.moodMesh.scale, {
          duration: .25,
          x: 1,
          y: 1
        });
      }
    });
  }

  showUserOptions() {
    if (!this.model.streamController.isInRoom()) {
      return;
    }
    $(".cta-menu").removeClass("is-active");
    if (this.iSitOnIt) {
      $("#own-chair-menu").addClass("is-active");
    } else {
      $("#peer-chair-menu").addClass("is-active");
    }
    $("#peerIsFriend").hide();
    $("#peerAddAsFriend").hide();
    if (this.model.config.userId === this.userId) {
      return
    }
    if (this.userId && !this.model.friendsMap[this.userId]) {
      $("#peerIsFriend").hide();
      $("#peerAddAsFriend").show();
    } else {
      $("#peerIsFriend").show();
      $("#peerAddAsFriend").hide();
    }
  }

  hideUserOptions() {
    super.hideUserOptions();
    $("#own-chair-menu").removeClass("is-active");
    $("#peer-chair-menu").removeClass("is-active");
  }

  showInfo() {
    if (!this.userInfo)
      return;
    this.model.showUserInfo({ data: { ...this.userInfo, wpId: this.userInfo.hasProfile }, eventOrigin: '3Dobject' });
    this.hideUserOptions();
  }

  addAsFriend() {
    this.model.worldFetcher.addAsFriend(this.userId, (response) => {
      this.model.onFriendAddedSuccess(response);
    });
  }

  getState() {
    let user;
    const _occupiedBy = this.isSeatOccupiedByUser();
    const inRoom = this.model.streamController.isInRoom();
    if (!_occupiedBy) {
      this.state =
      {
        occupied: false,
        me: false,
        videoOn: true,
        avatarImage: null,
        screenShareStreamId: null,
        videosInfo: null,
      }
      return this.state;

    } if (_occupiedBy) {
      if (_occupiedBy === this.model.config.userId) {
        this.state = {
          occupied: true,
          me: true,
          videoOn: true,
          avatarImage: this.model.config.userAvatarUrl,
          videosInfo: this.model.videosInfo,
        }
      } else {
        const data = this.model.socket.getUsersObject()[_occupiedBy];
        user = data;
        const { userAvatarUrl, screenShareStreamId, videosInfo } = data;
        this.state = {
          occupied: true,
          me: false,
          videoOn: inRoom ? true : false,
          avatarImage: userAvatarUrl,
          screenShareStreamId: screenShareStreamId,
          videosInfo,
        }
      }
      console.log(this.state);
    }
    return this.state;
  }

  showRightMaterial() {
    const inRoom = this.model.streamController.isInRoom();
    console.log(this.getState())
    console.log("this.state")
    if (this.state.occupied) {
      this.mesh.visible = true;
      window.screenShareMesh = this;
      if (this.state.me) {
        if (this.state.videoOn) {
          this.handleUserWantsCamera();
          this.mesh.material = this.videoMaterial;
          // if (webrtc.videoMode === "screen+camera") {
          //   this.mesh.material = this.leftImageMaterial;
          //   if (this.state.videosInfo && this.videoElement) {
          //     const { camera: { height } } = this.state.videosInfo
          //     const aspectRatio = this.videoElement.videoHeight / height;
          //     this.videoMaterial.uniforms.ratio.value = aspectRatio / 2;
          //   }
          // }
        } else {
          this.handleUserWantsAvatar();
        }
        return;
      } if (inRoom) {
        if (this.state.videoOn) {
          this.startVideoStream(this.state.screenShareStreamId);
          // this.mesh.material = this.videoMaterial;
        }
      }
      else {
        this.setAvatarImage(this.state.avatarImage)
      }
    } else {
      //not occupied
      this.mesh.visible = false;
    }
  }

  onScreenStreamEnded = () => {
    this.model.dispatchEvent({ type: WEBRTC_EVENTS.USER_STOP_SCREEN_MANUALLY })
  }


  setScreenGeometry = () => {
    // console.log('setScreenGeometry  ',this.screenStream.getSettings());
    if (!this.videoElement || !this.videoElement.srcObject) {
      return;
    }
    this.stream = this.videoElement.srcObject;
    this.screenStream = this.videoElement.srcObject.getVideoTracks()[0];
    let aspectRatio = this.videoElement.srcObject.getVideoTracks()[0].getSettings().aspectRatio;
    this.screenStream.removeEventListener('ended', this.onScreenStreamEnded);
    this.screenStream.addEventListener('ended', this.onScreenStreamEnded)
    if (aspectRatio == undefined) {
      aspectRatio = 16 / 9;
    }
    let w = 1;
    let h = 1;

    if (aspectRatio < 1) {
      // portrait
      w = 360 * aspectRatio;
      h = 360;
    } else {
      // landscape
      w = 480;
      h = 480 / aspectRatio;
    }

    // prevent redundant geometry rebuild if nothing changed
    const nextGeometryKey = `${aspectRatio}|${this.borderThickness}`;
    if (this._lastGeometryKey === nextGeometryKey) {
      return;
    }

    this.lastKnownAspectRatio = aspectRatio;

    let videoGeometry = this.getBorder(w, h, 2);

    this.mesh.menuPosition.position.y = 0;
    this.mesh.menuPosition.translateY((h / 2 + 15) * -1);

    this.mesh.geometry = new BufferGeometry();
    this.mesh.geometry.setAttribute('position', new BufferAttribute(new Float32Array(videoGeometry[0]), 3));
    this.mesh.geometry.setAttribute('uv', new BufferAttribute(new Float32Array(videoGeometry[1]), 2));

    let videoBorderGeometry = this.getBorder(w + 4, h + 4, 2);
    // use configurable thickness
    videoBorderGeometry = this.getBorder(w + this.borderThickness, h + this.borderThickness, 2);
    this.borderMesh.geometry = new BufferGeometry();
    this.borderMesh.geometry.setAttribute('position', new BufferAttribute(new Float32Array(videoBorderGeometry[0]), 3));
    this.borderMesh.geometry.setAttribute('uv', new BufferAttribute(new Float32Array(videoBorderGeometry[1]), 2));
    this.mesh.add(this.borderMesh);
    this.borderMesh.translateZ(-2);
    this.borderMesh.visible = true;
    window.screenShareMesh = this;

    this.mesh.material.needsUpdate = true;

    // update cache key after successful rebuild
    this._lastGeometryKey = nextGeometryKey;

  }

}
export { VRScreenShare };